import React, { useState, useMemo, useEffect, useRef } from 'react';
import { Input } from '../Input';
import { fromDecimalToMoney, MAX_SAFE_DECIMAL, Money, toDecimalFromMoney } from '../../../lib/Money';
import { useCurrencyHelpers } from './hooks/useCurrencyHelpers';
import type { CurrencyEnum } from '../../../generated/enums.ts';

export interface MoneyInputProps {
  value?: Money | null;
  onChange?: (value: Money | null) => void;
  onFocus?: (e: React.FocusEvent<HTMLInputElement>) => void;
  onBlur?: (e: React.FocusEvent<HTMLInputElement>) => void;
  locale?: string;
  currency?: CurrencyEnum;
  maximumFractionDigits?: number;
  minimumFractionDigits?: number;
  className?: string;
  disabled?: boolean;
  readOnly?: boolean;
  enforceMaxSafeDecimal?: boolean;
  selectOnFocus?: boolean;
  allowEmpty?: boolean;
  placeholder?: string;
  'aria-invalid'?: boolean;
  'aria-label'?: string;
  'aria-labelledby'?: string;
}

export const MoneyInput: React.FC<MoneyInputProps> = ({
  value,
  onChange,
  onFocus,
  onBlur,
  locale = 'nl-NL',
  currency,
  maximumFractionDigits = 2,
  minimumFractionDigits = 2,
  className = '',
  disabled = false,
  readOnly = false,
  enforceMaxSafeDecimal = true,
  selectOnFocus = false,
  allowEmpty = false,
  placeholder,
  'aria-invalid': ariaInvalid,
  'aria-label': ariaLabel,
  'aria-labelledby': ariaLabelledBy,
}) => {
  const resolvedCurrency = value?.currency ?? currency ?? 'EUR';
  const {
    format,
    toEditableFromNumber,
    sanitizeInput,
    parseToNumber,
  } = useCurrencyHelpers({
    locale,
    maximumFractionDigits,
    minimumFractionDigits,
    currency: resolvedCurrency,
  });

  const emptyDisplay = allowEmpty ? '' : format(0);

  const [internalValue, setInternalValue] = useState<string>(() =>
    value ? format(toDecimalFromMoney(value)) : emptyDisplay
  );

  const isEditingRef = useRef(false);
  const prevValueRef = useRef<Money | null | undefined>(value);
  const inputRef = useRef<HTMLInputElement>(null);

  const expectedFormattedValue = useMemo(() => {
    if (!value) return emptyDisplay;
    return format(toDecimalFromMoney(value));
  }, [value, format, emptyDisplay]);

  useEffect(() => {
    if (isEditingRef.current) return;

    const prevValue = prevValueRef.current;
    const valueChanged =
      !prevValue ||
      prevValue.signed_amount !== value?.signed_amount ||
      prevValue.currency !== value?.currency;

    if (valueChanged) {
      setInternalValue(expectedFormattedValue);
      prevValueRef.current = value;
    }
  }, [value, expectedFormattedValue]);

  const handleFocus = (e: React.FocusEvent<HTMLInputElement>) => {
    isEditingRef.current = true;
    if (value) {
      setInternalValue(toEditableFromNumber(toDecimalFromMoney(value)));
    } else {
      setInternalValue(allowEmpty ? '' : toEditableFromNumber(0));
    }
    if (selectOnFocus) {
      requestAnimationFrame(() => inputRef.current?.select());
    }
    onFocus?.(e);
  };

  const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    isEditingRef.current = false;
    if (allowEmpty && internalValue.trim() === '') {
      setInternalValue('');
      prevValueRef.current = value;
      onBlur?.(e);
      return;
    }
    const numberValue = parseToNumber(internalValue);
    const formatted = format(numberValue);
    setInternalValue(formatted);
    prevValueRef.current = value;
    onBlur?.(e);
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const sanitized = sanitizeInput(e.target.value);

    if (allowEmpty && sanitized.trim() === '') {
      setInternalValue(sanitized);
      onChange?.(null);
      return;
    }

    const amount = parseToNumber(sanitized);

    if (enforceMaxSafeDecimal && Math.abs(amount) > MAX_SAFE_DECIMAL) {
      return;
    }

    setInternalValue(sanitized);
    const next = fromDecimalToMoney(amount, resolvedCurrency);
    onChange?.(next);
  };

  return (
    <Input
      ref={inputRef}
      type="text"
      inputMode="decimal"
      value={internalValue}
      placeholder={placeholder}
      onChange={handleChange}
      onFocus={handleFocus}
      onBlur={handleBlur}
      className={className}
      disabled={disabled}
      readOnly={readOnly}
      aria-invalid={ariaInvalid}
      aria-label={ariaLabel}
      aria-labelledby={ariaLabelledBy}
    />
  );
};

export default MoneyInput;
